﻿import { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, CheckCheck, ChevronRight, X } from "lucide-react";
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllRead,
} from "../hooks/useNotifications";
import { useIsMobile } from "../hooks/useIsMobile";
import { Sheet } from "./Sheet";
import type { NotificationCategory } from "../api/types";

const CATEGORY_COLORS: Record<NotificationCategory, string> = {
  goal_achieved:        "bg-emerald-500",
  expense_threshold:    "bg-amber-500",
  payment_due:          "bg-orange-500",
  payment_overdue:      "bg-red-500",
  appointment_upcoming: "bg-blue-500",
  reminder_upcoming:    "bg-purple-500",
  calendar_change:      "bg-indigo-500",
  sales_activity:       "bg-teal-500",
  deadline_proximity:   "bg-rose-500",
  system:               "bg-slate-400",
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

interface Props {
  onClose: () => void;
}

export function NotificationPanel({ onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const panelRef = useRef<HTMLDivElement>(null);
  const { data } = useNotifications({ status: "unread" });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllRead();
  const notifications = data?.results?.slice(0, 5) ?? [];

  useEffect(() => {
    // Desktop dropdown closes on outside click; the mobile Sheet handles its
    // own backdrop/Esc dismissal, so skip the global listener there.
    if (isMobile) return;
    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose, isMobile]);

  function handleNotificationClick(notif: { id: number; related_url: string | null; status: string }) {
    if (notif.status === "unread") {
      markRead.mutate(notif.id);
    }
    if (notif.related_url) {
      navigate(notif.related_url);
    }
    onClose();
  }

  const body = (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-fg-secondary" />
          <span className="text-sm font-semibold text-fg-primary">
            {t("notifications.title")}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {notifications.length > 0 && (
            <button
              onClick={() => markAll.mutate()}
              className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-fg-secondary hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title={t("notifications.markAllRead")}
            >
              <CheckCheck className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Notification list */}
      <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-72 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center px-4">
            <Bell className="h-7 w-7 text-slate-200 dark:text-slate-700 mb-2" />
            <p className="text-sm text-fg-muted">
              {t("notifications.noUnread")}
            </p>
          </div>
        ) : (
          notifications.map((notif) => (
            <button
              key={notif.id}
              onClick={() => handleNotificationClick(notif)}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors text-left"
            >
              <span
                className={`flex-shrink-0 mt-1 h-2 w-2 rounded-full ${
                  notif.status === "unread"
                    ? CATEGORY_COLORS[notif.category]
                    : "bg-slate-200 dark:bg-slate-600"
                }`}
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-fg-primary truncate">
                  {notif.title}
                </p>
                <p className="text-xs text-fg-secondary mt-0.5 line-clamp-2">
                  {notif.message}
                </p>
                <p className="text-xs text-fg-muted mt-0.5">
                  {timeAgo(notif.created_at)}
                </p>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-2.5">
        <button
          onClick={() => { navigate("/notifications"); onClose(); }}
          className="flex items-center justify-center gap-1.5 w-full text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
        >
          {t("notifications.viewAll")}
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </>
  );

  // On phones the fixed 320px dropdown overflows; render it as a bottom sheet.
  // The panel keeps its own header (mark-all + close), so hide the Sheet chrome.
  if (isMobile) {
    return (
      <Sheet open onClose={onClose} hideHeader testId="notification-sheet">
        {body}
      </Sheet>
    );
  }

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl ring-1 ring-slate-200/80 dark:ring-slate-700/80 z-50 overflow-hidden"
    >
      {body}
    </div>
  );
}
